/**
 * Lag-oppsett for basiskartet.
 *
 * Rekkefølge (bakerst → forrest):
 *   water → landcover(wood/grass) → landuse → park →
 *   buildings(3D) → veier(casing+fyll, zoom-avhengig) → rail →
 *   labels(steder + utvalgte veinavn)
 *
 * Kilde: OpenMapTiles-skjema (source-layer-navn verifisert mot
 * OpenFreeMap planet-tilejson).
 *
 * Prinsipp: lav informasjonsstøy. Generelle POI-er utelates helt.
 */

import { PALETTE } from './style.js';

const SRC = 'openmaptiles';

/**
 * Interpoler bredde etter zoom.
 * stops: [[zoom, width], ...]
 */
function widthByZoom(stops) {
  return {
    type: 'exponential',
    base: 1.4,
    stops,
  };
}

// Fysisk veibredde: gir en line-width som tilsvarer `meters` på bakken ved
// høy zoom, så vei og kjøretøy (som tegnes i ekte meter) deler skala – en
// 2,5 m buss får da ~2,5× sin egen bredde å kjøre på, som i virkeligheten.
//
// Meter→piksler ved breddegrad ~59,9° (Bærum–Oslo): cos(59.9°) ≈ 0,50.
// px = meters · 2^zoom / (C · cos(lat)),  C = 156543,03 (verdensbredde/px v/ z0).
// Vi lager stopp ved z16/z18/z20/z22 og lar interpoleringen (base 2) holde
// bredden fysisk konstant. `minPx` er en gulvbredde på lav zoom så veien
// ikke forsvinner i oversikten.
const COS_LAT = 0.50;
// Global finjustering av alle veibredder. 1.0 = fysisk korrekt meter. Litt
// over 1 gjør veiene bredere/mer lesbare uten å endre hver enkelt verdi.
const ROAD_WIDTH_SCALE = 2.8;
function metersToPx(meters, zoom) {
  return (meters * ROAD_WIDTH_SCALE * 2 ** zoom) / (156543.03 * COS_LAT);
}
function metersWide(meters, minPx = 0.8) {
  // Fysisk meter-bredde opp til z18. OVER z18 flates bredden ut (holdes lik
  // z18-verdien i piksler): den overzoomede basiskart-geometrien (OpenFreeMap
  // stopper ~z14) blir grov, og linjer bredere enn ~100 px gir stygge join-
  // artefakter i svinger/skjøter der de «mister formen». Vi fryser derfor
  // bredden fra z18 i stedet for å la den vokse til hundrevis av piksler.
  const CAP_ZOOM = 18;
  const at = (z) => Math.max(minPx, metersToPx(meters, Math.min(z, CAP_ZOOM)));
  const stops = [11, 13, 14, 16, 18, 20, 22].map((z) => [z, at(z)]);
  return { type: 'exponential', base: 2, stops };
}

// --- Vann ---------------------------------------------------------------
function waterLayers() {
  return [
    {
      id: 'water',
      type: 'fill',
      source: SRC,
      'source-layer': 'water',
      paint: {
        'fill-color': PALETTE.water,
        'fill-antialias': true,
      },
    },
    {
      id: 'waterway',
      type: 'line',
      source: SRC,
      'source-layer': 'waterway',
      minzoom: 11,
      paint: {
        'line-color': PALETTE.waterDeep,
        'line-width': widthByZoom([
          [11, 0.4],
          [16, 1.6],
        ]),
      },
    },
  ];
}

// --- Brygger / kaikant / gangdekke -------------------------------------
// --- Brygger, kaikant og gangdekke/torg --------------------------------
// Verifisert mot faktisk tile: relevante flater i transportation-laget:
//   class='pier'                        – ekte kaikonstruksjon over vann
//   class='path', subclass='pedestrian' – gangplasser OG torg (f.eks.
//                                          Aker Brygge-dekke og Rådhusplassen)
//   class='path', subclass='platform'   – plattform/dekke
//
// pedestrian brukes både for bryggedekke (over vann) og for torg (på
// land), uten attributt som skiller dem. Vi gir derfor:
//   - ekte 'pier'         → varm bryggebrun
//   - pedestrian/platform → nøytralt gangdekke/torg
// Slik blir Rådhusplassen riktig, mens bryggene fortsatt ligger som
// flater oppå vannet så byggene ikke "drukner".
function pierLayers() {
  return [
    // Nøytralt gangdekke/torg (ligger under ekte pier i rekkefølgen).
    {
      id: 'plaza',
      type: 'fill',
      source: SRC,
      'source-layer': 'transportation',
      minzoom: 13,
      filter: [
        'all',
        ['==', ['get', 'class'], 'path'],
        ['in', ['get', 'subclass'], ['literal', ['pedestrian', 'platform']]],
      ],
      paint: {
        'fill-color': PALETTE.plaza,
        'fill-opacity': 0.9,
      },
    },
    // Ekte brygge/kaikonstruksjon – varm brun.
    {
      id: 'pier',
      type: 'fill',
      source: SRC,
      'source-layer': 'transportation',
      minzoom: 13,
      filter: ['==', ['get', 'class'], 'pier'],
      paint: {
        'fill-color': PALETTE.pier,
        'fill-opacity': 0.95,
      },
    },
  ];
}

// --- Vegetasjon / landcover / landuse ----------------------------------
function landLayers() {
  return [
    // Skog / wood – tydelig men dempet grønn (flate polygoner).
    {
      id: 'landcover-wood',
      type: 'fill',
      source: SRC,
      'source-layer': 'landcover',
      filter: ['in', ['get', 'class'], ['literal', ['wood', 'forest']]],
      paint: {
        'fill-color': PALETTE.forest,
        'fill-opacity': [
          'interpolate', ['linear'], ['zoom'],
          8, 0.55,
          13, 0.7,
        ],
      },
    },
    // Gress / eng
    {
      id: 'landcover-grass',
      type: 'fill',
      source: SRC,
      'source-layer': 'landcover',
      filter: ['in', ['get', 'class'], ['literal', ['grass', 'meadow', 'scrub']]],
      paint: {
        'fill-color': PALETTE.grass,
        'fill-opacity': 0.6,
      },
    },
    // Generell landuse (residential/industrial) – veldig svakt.
    {
      id: 'landuse',
      type: 'fill',
      source: SRC,
      'source-layer': 'landuse',
      filter: ['in', ['get', 'class'], ['literal', ['residential', 'industrial', 'commercial']]],
      minzoom: 11,
      paint: {
        'fill-color': PALETTE.landuse,
        'fill-opacity': 0.5,
      },
    },
    // Parker
    {
      id: 'park',
      type: 'fill',
      source: SRC,
      'source-layer': 'park',
      paint: {
        'fill-color': PALETTE.grass,
        'fill-opacity': 0.65,
      },
    },
  ];
}

// --- Bygninger (3D fill-extrusion) -------------------------------------

// Punkt der 3D-modellen (klokketårnet) plasseres.
export const CLOCKTOWER_LNGLAT = [10.728272, 59.909827];

function buildingLayers() {
  return [
    {
      id: 'buildings-3d',
      type: 'fill-extrusion',
      source: SRC,
      'source-layer': 'building',
      minzoom: 13,
      paint: {
        'fill-extrusion-color': [
          'interpolate', ['linear'],
          // Bruk coalesce så manglende render_height (null) ikke gir
          // svart farge – null som interpolate-input kollapser ellers.
          ['coalesce', ['get', 'render_height'], 8],
          0, PALETTE.building,
          40, PALETTE.buildingTop,
        ],
        // Bruk høyde fra tiles, fallback til fornuftig verdi når den mangler/er 0.
        'fill-extrusion-height': [
          'case',
          ['>', ['coalesce', ['get', 'render_height'], 0], 0],
          ['get', 'render_height'],
          8, // fallback-høyde
        ],
        'fill-extrusion-base': ['coalesce', ['get', 'render_min_height'], 0],
        'fill-extrusion-opacity': 0.9,
        // Fade inn mykt når vi zoomer inn.
        'fill-extrusion-vertical-gradient': true,
      },
    },
  ];
}

// --- Veihierarki -------------------------------------------------------
// class-verdier (OpenMapTiles transportation):
//   motorway, trunk, primary, secondary, tertiary, minor,
//   service, path, track, rail, ...
function roadLayers() {
  const isTunnel = ['!=', ['get', 'brunnel'], 'tunnel'];

  // Stableorden i flerplanskryss.
  //
  // OSM merker hvilket plan en vei ligger i med `layer` (-3..+3), og
  // OpenMapTiles tar attributtet med videre – verifisert i flisene over
  // E18/E16-krysset ved Sandvika, som har layer 1, -1 og -3 side om side.
  // Uten sorteringsnøkkel tegner MapLibre i den rekkefølgen flisen tilfeldigvis
  // gir, og en rampe kan havne oppå motorveien den egentlig går under.
  //
  // `line-sort-key` sorterer BARE innenfor ett stillag. At en bru går over en
  // vei på bakkeplan er allerede dekket av bru-passet i route.js, som tegner
  // alle bruer på nytt over rutene. Det som fikses her er det som gjenstår:
  // ramper i samme veiklasse som krysser hverandre i ulike plan.
  const byLayer = ['coalesce', ['get', 'layer'], 0];

  return [
    // Stier / footway – svakest, kun høy zoom.
    {
      id: 'road-path',
      type: 'line',
      source: SRC,
      'source-layer': 'transportation',
      minzoom: 13.5,
      filter: ['all', ['in', ['get', 'class'], ['literal', ['path', 'track']]]],
      layout: { 'line-cap': 'round', 'line-join': 'round', 'line-sort-key': byLayer },
      paint: {
        'line-color': PALETTE.roadPath,
        'line-dasharray': [2, 2],
        'line-width': widthByZoom([[13.5, 1.0], [16, 1.8], [20, 4]]),
      },
    },
    // Service – kun høy zoom.
    {
      id: 'road-service',
      type: 'line',
      source: SRC,
      'source-layer': 'transportation',
      minzoom: 13.5,
      filter: ['all', isTunnel, ['==', ['get', 'class'], 'service']],
      layout: { 'line-cap': 'round', 'line-join': 'round', 'line-sort-key': byLayer },
      paint: {
        'line-color': PALETTE.roadService,
        'line-width': metersWide(4, 1.0),
      },
    },

    // Residential – casing + fyll, fra middels/høy zoom.
    {
      id: 'road-residential-casing',
      type: 'line',
      source: SRC,
      'source-layer': 'transportation',
      minzoom: 12,
      filter: ['all', isTunnel, ['in', ['get', 'class'], ['literal', ['minor', 'residential']]]],
      layout: { 'line-cap': 'round', 'line-join': 'round', 'line-sort-key': byLayer },
      paint: {
        'line-color': PALETTE.roadResidentialCasing,
        'line-width': metersWide(7.5, 2.2),
        'line-opacity': 0.6,
      },
    },
    {
      id: 'road-residential',
      type: 'line',
      source: SRC,
      'source-layer': 'transportation',
      minzoom: 12,
      filter: ['all', isTunnel, ['in', ['get', 'class'], ['literal', ['minor', 'residential']]]],
      layout: { 'line-cap': 'round', 'line-join': 'round', 'line-sort-key': byLayer },
      paint: {
        'line-color': PALETTE.roadResidential,
        'line-width': metersWide(6, 1.4),
      },
    },

    // Tertiary
    {
      id: 'road-tertiary',
      type: 'line',
      source: SRC,
      'source-layer': 'transportation',
      minzoom: 12,
      filter: ['all', isTunnel, ['==', ['get', 'class'], 'tertiary']],
      layout: { 'line-cap': 'round', 'line-join': 'round', 'line-sort-key': byLayer },
      paint: {
        'line-color': PALETTE.roadTertiary,
        'line-width': metersWide(6.5, 0.9),
      },
    },

    // Secondary – casing + fyll.
    {
      id: 'road-secondary-casing',
      type: 'line',
      source: SRC,
      'source-layer': 'transportation',
      minzoom: 11,
      filter: ['all', isTunnel, ['==', ['get', 'class'], 'secondary']],
      layout: { 'line-cap': 'round', 'line-join': 'round', 'line-sort-key': byLayer },
      paint: {
        'line-color': PALETTE.roadSecondaryCasing,
        'line-width': metersWide(9, 1.6),
        'line-opacity': 0.6,
      },
    },
    {
      id: 'road-secondary',
      type: 'line',
      source: SRC,
      'source-layer': 'transportation',
      minzoom: 11,
      filter: ['all', isTunnel, ['==', ['get', 'class'], 'secondary']],
      layout: { 'line-cap': 'round', 'line-join': 'round', 'line-sort-key': byLayer },
      paint: {
        'line-color': PALETTE.roadSecondary,
        'line-width': metersWide(7, 1),
      },
    },

    // Primary – casing + fyll.
    {
      id: 'road-primary-casing',
      type: 'line',
      source: SRC,
      'source-layer': 'transportation',
      minzoom: 9,
      filter: ['all', isTunnel, ['==', ['get', 'class'], 'primary']],
      layout: { 'line-cap': 'round', 'line-join': 'round', 'line-sort-key': byLayer },
      paint: {
        'line-color': PALETTE.roadPrimaryCasing,
        'line-width': metersWide(11.5, 1.6),
        'line-opacity': 0.7,
      },
    },
    {
      id: 'road-primary',
      type: 'line',
      source: SRC,
      'source-layer': 'transportation',
      minzoom: 9,
      filter: ['all', isTunnel, ['==', ['get', 'class'], 'primary']],
      layout: { 'line-cap': 'round', 'line-join': 'round', 'line-sort-key': byLayer },
      paint: {
        'line-color': PALETTE.roadPrimary,
        'line-width': metersWide(9, 1),
      },
    },

    // Motorway / trunk – tydeligst og bredest, synlig fra lav zoom.
    {
      id: 'road-major-casing',
      type: 'line',
      source: SRC,
      'source-layer': 'transportation',
      minzoom: 7,
      filter: ['all', isTunnel, ['in', ['get', 'class'], ['literal', ['motorway', 'trunk']]]],
      layout: { 'line-cap': 'round', 'line-join': 'round', 'line-sort-key': byLayer },
      paint: {
        'line-color': PALETTE.roadMajorCasing,
        'line-width': metersWide(15, 1.4),
      },
    },
    {
      id: 'road-major',
      type: 'line',
      source: SRC,
      'source-layer': 'transportation',
      minzoom: 7,
      filter: ['all', isTunnel, ['in', ['get', 'class'], ['literal', ['motorway', 'trunk']]]],
      layout: { 'line-cap': 'round', 'line-join': 'round', 'line-sort-key': byLayer },
      paint: {
        'line-color': PALETTE.roadMajor,
        'line-width': metersWide(12, 0.8),
      },
    },

    // Jernbane
    {
      id: 'rail',
      type: 'line',
      source: SRC,
      'source-layer': 'transportation',
      minzoom: 11,
      filter: ['all', isTunnel, ['==', ['get', 'class'], 'rail']],
      layout: { 'line-cap': 'butt', 'line-join': 'round', 'line-sort-key': byLayer },
      paint: {
        'line-color': PALETTE.rail,
        'line-width': widthByZoom([[11, 0.6], [18, 2.2]]),
        'line-dasharray': [3, 2],
      },
    },
  ];
}

// --- Labels ------------------------------------------------------------
// Kun et lite, kuratert utvalg stedsnavn for lav informasjonsstøy.
// Bærums Verk og Vika er tatt ut: endepunktene bæres av de svevende
// skiltene i place-signs.js, og doble navn samme sted blir bare støy.
// Mellomstedene beholdes – de er det som gjør ruta gjenkjennelig underveis.
const PRIORITY_PLACES = [
  'Bærum',
  'Sandvika',
  'Lysaker',
  'Skøyen',
  'Oslo',
  'Aker Brygge',
  'Oslofjorden',
];

function labelLayers() {
  return [
    // Vannnavn (f.eks. Oslofjorden) – egen dempet blåtone.
    {
      id: 'label-water',
      type: 'symbol',
      source: SRC,
      'source-layer': 'water_name',
      filter: ['in', ['coalesce', ['get', 'name:no'], ['get', 'name']], ['literal', PRIORITY_PLACES]],
      layout: {
        'text-field': ['coalesce', ['get', 'name:no'], ['get', 'name']],
        'text-font': ['Noto Sans Italic'],
        'text-size': ['interpolate', ['linear'], ['zoom'], 9, 11, 14, 15],
        'text-letter-spacing': 0.1,
        'text-max-width': 8,
      },
      paint: {
        'text-color': PALETTE.labelWater,
        'text-halo-color': PALETTE.water,
        'text-halo-width': 1,
        'text-opacity': 0.9,
      },
    },
    // Stedsnavn – gradvis innfasing etter zoom, kun prioriterte navn.
    {
      id: 'label-place',
      type: 'symbol',
      source: SRC,
      'source-layer': 'place',
      filter: ['in', ['coalesce', ['get', 'name:no'], ['get', 'name']], ['literal', PRIORITY_PLACES]],
      layout: {
        'text-field': ['coalesce', ['get', 'name:no'], ['get', 'name']],
        'text-font': ['Noto Sans Regular'],
        // Større byer (lavere rank) kommer inn først / større.
        'text-size': [
          'interpolate', ['linear'], ['zoom'],
          8, ['case', ['<=', ['coalesce', ['get', 'rank'], 10], 4], 14, 0],
          11, ['case', ['<=', ['coalesce', ['get', 'rank'], 10], 8], 14, 11],
          14, 16,
        ],
        'text-letter-spacing': 0.05,
        'text-max-width': 7,
        'text-transform': 'none',
      },
      paint: {
        'text-color': PALETTE.labelText,
        'text-halo-color': PALETTE.labelHalo,
        'text-halo-width': 1.4,
      },
    },
  ];
}

/**
 * Setter sammen alle basiskart-lag i riktig rekkefølge.
 * (background legges av style.js foran disse.)
 *
 * Merk rekkefølgen veier → bygg: fill-extrusion (bygg) skriver til
 * dybdebufferen og tegnes ETTER de flate veilinjene, slik at bygg
 * dekker (okkluderer) veier som ligger bak dem sett fra kameraet.
 * Flate linjer har ingen egen z-høyde, så uten dette "lyser" stier
 * og veier gjennom byggene ved pitch.
 */
export function buildBaseLayers() {
  return [
    ...waterLayers(),
    ...pierLayers(),
    ...landLayers(),
    ...roadLayers(),
    ...buildingLayers(),
    ...labelLayers(),
  ];
}

// Eksporter id-en til øverste basiskart-lag slik at rute-laget
// kan settes rett under labels om ønskelig.
export const FIRST_LABEL_LAYER_ID = 'label-water';
